/**
 * Straturile de acoperire, ca functie pura: din punctele survey-ului (sau din
 * AP-urile puse pe plan, pentru predictie) iese o grila colorata. O ruleaza
 * `coverage.worker.ts` in afara firului paginii; probele o cheama direct.
 */
import { coverageGrid, gridToRgba, type Sample, type Thresholds } from './idw'
import { bestPredicted, predictCell, type ApM, type WallM } from './predict'

export type CoverageRequest =
  | {
      kind: 'survey'
      widthM: number
      heightM: number
      cellM: number
      maskM: number
      thresholds: Thresholds
      samples: Sample[]
    }
  | {
      kind: 'predict'
      widthM: number
      heightM: number
      cellM: number
      thresholds: Thresholds
      aps: ApM[]
      walls: WallM[]
    }

export type CoverageResult = { cols: number; rows: number; cell: number; rgba: Uint8ClampedArray }

export function computeCoverage(req: CoverageRequest): CoverageResult {
  if (req.kind === 'survey') {
    const grid = coverageGrid(req.samples, req.widthM, req.heightM, req.cellM, req.maskM)
    return { cols: grid.cols, rows: grid.rows, cell: grid.cell, rgba: gridToRgba(grid, req.thresholds) }
  }
  const cell = predictCell(req.widthM, req.heightM, req.cellM, req.aps.length, req.walls.length)
  const cols = Math.max(1, Math.ceil(req.widthM / cell))
  const rows = Math.max(1, Math.ceil(req.heightM / cell))
  const values = new Float32Array(cols * rows).fill(Number.NaN)
  if (req.aps.length > 0) {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const v = bestPredicted(req.aps, { x: (c + 0.5) * cell, y: (r + 0.5) * cell }, req.walls)
        if (v !== null) values[r * cols + c] = v
      }
    }
  }
  return { cols, rows, cell, rgba: gridToRgba({ cols, rows, cell, values }, req.thresholds) }
}

/** O citire a unui punct de survey, cum vine de la server. */
export type Reading = { bssid: string; rssi_dbm: number; connected?: boolean }

export type BssidAp = { key: string; name: string | null; known: boolean }

/**
 * Valoarea unui punct masurat:
 *  - `best`: cel mai bun semnal dintre AP-urile FIRMEI (cele din lista de
 *    AP-uri); vecinii nu acopera biroul. Daca niciun BSSID nu e cunoscut
 *    (lista de AP-uri e goala), se ia cel mai bun semnal vazut.
 *  - cheia unui AP: cel mai bun semnal dintre BSSID-urile lui.
 * null = nimic de spus in punctul asta.
 */
export function spotValue(readings: Reading[], bssidAps: Record<string, BssidAp>, ap: 'best' | string): number | null {
  let best: number | null = null
  const anyKnown = readings.some((r) => bssidAps[r.bssid]?.known)
  for (const r of readings) {
    const owner = bssidAps[r.bssid]
    if (ap === 'best') {
      if (anyKnown && !owner?.known) continue
    } else if (owner?.key !== ap) {
      continue
    }
    if (best === null || r.rssi_dbm > best) best = r.rssi_dbm
  }
  return best
}
